import { useState, useEffect, useRef } from "react";
import "./Selector.css";
import { getDefaultUserAvatar, iconPath } from "winslow-core";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/features/appData";

type Option = {
  value: string;
  label: string;
  imageUrl?: string; // Optional URL for an image
  color?: string; // Optional hex color for SVG circle
};

type SelectorProps = {
  options: Option[];
  onChange: (value: string) => void;
  roundIcons?: boolean;
  placeholder?: string;
  openOnTop?: boolean;
  disabled?: boolean;
  selectedValue?: string;
  preventOpen?: boolean;
  onPreventedOpen?: () => void;
  value?: string;
};

export const Selector = ({
  options,
  onChange,
  roundIcons = false,
  placeholder = "(no filter)",
  openOnTop = false,
  disabled = false,
  selectedValue: initialValue,
  preventOpen = false,
  onPreventedOpen,
  value,
}: SelectorProps) => {
  const storeUser = useSelector(selectUser);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    value || initialValue
  );

  const selectorRef = useRef<HTMLDivElement>(null);

  const handleSelect = (value: string) => {
    setSelectedValue(value);
    setIsOpen(false);
    onChange(value);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      selectorRef.current &&
      !selectorRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectorRef.current &&
        !selectorRef.current.contains(event.target as Node) &&
        isOpen
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    // Use capture phase to ensure we catch the click before other handlers
    document.addEventListener("mousedown", handleClickOutside, true);
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isOpen]); // Add isOpen to dependencies

  useEffect(() => {
    if (initialValue !== undefined) {
      setSelectedValue(initialValue);
    }
  }, [initialValue]);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const selectedOption = options.find(
    (option) => option.value === selectedValue
  );

  const handleClick = () => {
    if (disabled) {
      return;
    }

    if (preventOpen) {
      onPreventedOpen?.();
      return;
    }

    setIsOpen(!isOpen);
  };

  return (
    <div
      ref={selectorRef}
      className={`selector-container ${disabled ? "selector-disabled" : ""}`}>
      <div className="selector-selected" onClick={handleClick}>
        <span className="placeholder-text">
          {selectedOption?.label || placeholder || "(no filter)"}
        </span>
        <div className="selector-arrow">
          <img src={iconPath("down.svg", storeUser)} alt="" />
        </div>
      </div>
      {isOpen && !disabled && !preventOpen && (
        <div
          className={`selector-dropdown ${openOnTop ? "selector-dropdown-top" : "selector-dropdown-bottom"}`}>
          {options.map((option) => (
            <div
              key={option.value}
              className="selector-option"
              onClick={() => handleSelect(option.value)}>
              {option.imageUrl ? (
                <img
                  src={option.imageUrl}
                  alt={getDefaultUserAvatar(option.label)}
                  className={roundIcons ? "selector-avatar" : "selector-icon"}
                />
              ) : option.color ? (
                <SvgCircle color={option.color} size={40} />
              ) : null}
              <div className="selector-label">{option.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

type CircleProps = {
  color: string; // Prop to specify the fill color
  size?: number; // Optional prop to specify the SVG size (default is 100)
};

const SvgCircle: React.FC<CircleProps> = ({ color, size = 100 }) => {
  const radius = size / 2 - 5; // Calculate the radius based on size

  return (
    <svg
      height={size}
      width={size}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Colored circle">
      <circle r={radius} cx={size / 2} cy={size / 2} fill={color} />
    </svg>
  );
};

export default SvgCircle;
